import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import FinanceDateFilter from "../../components/finance/FinanceDateFilter";
import FinanceHelpLabel from "../../components/finance/FinanceHelpLabel";
import FinanceIncompleteBadge from "../../components/finance/FinanceIncompleteBadge";
import FinanceMetricCard from "../../components/finance/FinanceMetricCard";
import TrendChart from "../../components/finance/FinanceOverviewTrendChart";
import FinanceWarningBanner from "../../components/finance/FinanceWarningBanner";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import {
  financeRangeFromSearch,
  financeRangeSearch,
  type FinanceDateRangeValue,
} from "../../finance/dates";
import {
  formatMoney,
  formatPercent,
  knownMoney,
  previousPeriodComparison,
} from "../../finance/formatting";
import { fetchFinanceOverview } from "../../finance/overview-api";
import type { FinanceOverviewResponse } from "../../finance/overview-types";

function previousRangeMeta(report: FinanceOverviewResponse | null) {
  return report?.range
    ? { from: report.range.previous_from, to: report.range.previous_to }
    : null;
}

function warnings(report: FinanceOverviewResponse) {
  const items: string[] = [];
  const value = report.warnings;

  if (value.missing_price_shipments > 0) {
    items.push(`${value.missing_price_shipments} shipment prices`);
  }
  if (value.missing_cost_shipments > 0) {
    items.push(`${value.missing_cost_shipments} shipment material costs`);
  }
  if (value.unattributed_credit_items > 0) {
    items.push(`${value.unattributed_credit_items} unattributed credit items`);
  }
  if (value.missing_production_cost_inputs > 0) {
    items.push(
      `${value.missing_production_cost_inputs} production input costs`,
    );
  }
  if (value.wips_without_inputs > 0) {
    items.push(`${value.wips_without_inputs} completed WIPs without inputs`);
  }
  if (value.missing_receiving_cost_items > 0) {
    items.push(`${value.missing_receiving_cost_items} received item costs`);
  }
  if (value.missing_inventory_cost_items > 0) {
    items.push(`${value.missing_inventory_cost_items} inventory item costs`);
  }

  return items;
}

export default function FinanceOverview() {
  const [range, setRange] = useState<FinanceDateRangeValue>(() =>
    financeRangeFromSearch(window.location.search),
  );
  const [report, setReport] = useState<FinanceOverviewResponse | null>(null);
  const [currency, setCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const rangeQuery = financeRangeSearch(range);
  const previousRange = previousRangeMeta(report);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchFinanceOverview(range)
      .then((response) => {
        if (!active) {
          return;
        }
        setReport(response);
        setCurrency((current) => {
          const currencies = response.sales.summary.by_currency;
          return currencies.some((item) => item.currency === current)
            ? current
            : currencies[0]?.currency ?? "";
        });
      })
      .catch(() => {
        if (active) {
          setError("The Finance Overview could not be loaded.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [range.from, range.to]);

  const summary = report?.sales.summary.by_currency.find(
    (item) => item.currency === currency,
  );
  const previous = report?.sales.previous_summary.by_currency.find(
    (item) => item.currency === currency,
  );
  const topProducts = report?.top_products.find(
    (group) => group.currency === currency,
  )?.data;
  const topCustomers = report?.top_customers.find(
    (group) => group.currency === currency,
  )?.data;
  const warningItems = useMemo(() => (report ? warnings(report) : []), [report]);

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-700">Finance</p>
            <h1 className="text-2xl font-semibold text-slate-900">Overview</h1>
            <p className="mt-1 text-sm text-slate-600">
              Sales, material costs, supplier spending, customers, and current
              inventory value in one place.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            {report && report.sales.summary.by_currency.length > 1 && (
              <label className="text-sm font-medium text-slate-700">
                Currency
                <select
                  className="mt-1 block rounded-lg border border-slate-300 bg-white px-3 py-2"
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                >
                  {report.sales.summary.by_currency.map((item) => (
                    <option key={item.currency_id} value={item.currency}>
                      {item.currency}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <FinanceDateFilter value={range} onChange={setRange} />
          </div>
        </div>

        {warningItems.length > 0 && (
          <div className="mt-5">
            <FinanceWarningBanner>
              Review incomplete source data: {warningItems.join(", ")}. Known
              values are shown without assuming missing amounts are zero.
            </FinanceWarningBanner>
          </div>
        )}

        {error && (
          <p className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            {error}
          </p>
        )}

        <section className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <FinanceMetricCard
            label={`Net Sales${currency ? ` (${currency})` : ""}`}
            value={
              summary
                ? knownMoney(
                    summary.net_sales,
                    summary.known_net_sales,
                    summary.currency,
                  )
                : loading
                  ? "Loading…"
                  : "—"
            }
            comparison={previousPeriodComparison(
              summary?.net_sales ?? null,
              previous?.net_sales ?? null,
              previousRange,
            )}
            incomplete={summary?.net_sales === null}
          />
          <FinanceMetricCard
            label={`Material COGS${currency ? ` (${currency})` : ""}`}
            value={
              summary
                ? knownMoney(summary.cogs, summary.known_cogs, summary.currency)
                : loading
                  ? "Loading…"
                  : "—"
            }
            comparison={previousPeriodComparison(
              summary?.cogs ?? null,
              previous?.cogs ?? null,
              previousRange,
            )}
            incomplete={summary?.cogs === null}
          />
          <FinanceMetricCard
            label={`Gross Profit${currency ? ` (${currency})` : ""}`}
            value={
              summary
                ? knownMoney(
                    summary.gross_profit,
                    summary.known_net_sales - summary.known_cogs,
                    summary.currency,
                  )
                : loading
                  ? "Loading…"
                  : "—"
            }
            comparison={previousPeriodComparison(
              summary?.gross_profit ?? null,
              previous?.gross_profit ?? null,
              previousRange,
            )}
            incomplete={summary?.gross_profit === null}
          />
          <FinanceMetricCard
            label="Gross Margin"
            value={formatPercent(summary?.gross_margin)}
            comparison={previousPeriodComparison(
              summary?.gross_margin ?? null,
              previous?.gross_margin ?? null,
              previousRange,
            )}
            incomplete={summary?.gross_margin === null}
          />
          <FinanceMetricCard
            label={`Backlog${currency ? ` (${currency})` : ""}`}
            value={formatMoney(summary?.backlog, currency || "CAD")}
            comparison={`${summary?.open_order_count ?? 0} open orders`}
          />
          <FinanceMetricCard
            label="Receiving Spend"
            value={
              report
                ? knownMoney(
                    report.receiving.summary.spend,
                    report.receiving.summary.known_spend,
                  )
                : "—"
            }
            comparison={previousPeriodComparison(
              report?.receiving.summary.spend ?? null,
              report?.receiving.previous_summary.spend ?? null,
              previousRange,
            )}
            incomplete={report?.receiving.summary.complete === false}
          />
          <FinanceMetricCard
            label="Production Material Cost"
            value={
              report
                ? knownMoney(
                    report.production.summary.material_cost,
                    report.production.summary.known_material_cost,
                  )
                : "—"
            }
            comparison={`${report?.production.summary.completed_wips ?? 0} completed WIPs`}
            incomplete={report?.production.summary.complete === false}
          />
          <FinanceMetricCard
            label="Current Inventory Value"
            value={
              report
                ? knownMoney(
                    report.inventory.total_value,
                    report.inventory.known_total_value,
                  )
                : "—"
            }
            comparison="Current on-hand value; not date-filtered"
            incomplete={report?.inventory.complete === false}
          />
        </section>

        {report && currency && (
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-semibold text-slate-900">
                  Sales and profit trend ({currency})
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Up to six contiguous periods within the selected range.
                </p>
              </div>
              <Link
                className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                to={`/finance/sales${rangeQuery}`}
              >
                View sales
              </Link>
            </div>
            <div className="mt-4">
              <TrendChart report={report} currency={currency} />
            </div>
          </section>
        )}

        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h2 className="font-semibold text-slate-900">
                Top products by Gross Profit
              </h2>
              <Link
                className="text-sm font-medium text-emerald-700"
                to={`/finance/sales${rangeQuery}`}
              >
                View all
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-4 py-3">
                        <FinanceHelpLabel label="Product" />
                      </th>
                    <th className="px-4 py-3">
                        <FinanceHelpLabel label="Net Sales" />
                      </th>
                    <th className="px-4 py-3">
                        <FinanceHelpLabel label="Gross Profit" />
                      </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && (
                    <tr>
                      <td colSpan={3} className="p-2">
                        <TableLoadingSkeleton rows={5} columns={3} />
                      </td>
                    </tr>
                  )}
                  {(topProducts ?? []).map((product) => (
                    <tr key={`${product.product_id}-${product.plu}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">
                          {product.product_name || product.plu}
                        </p>
                        {!product.complete && <FinanceIncompleteBadge />}
                      </td>
                      <td className="px-4 py-3">
                        {knownMoney(
                          product.net_sales,
                          product.known_net_sales,
                          product.currency,
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {formatMoney(product.gross_profit, product.currency)}
                      </td>
                    </tr>
                  ))}
                  {!loading && (topProducts?.length ?? 0) === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-slate-500" colSpan={3}>
                        No shipped products in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <h2 className="font-semibold text-slate-900">
                Top customers by Gross Profit
              </h2>
              <Link
                className="text-sm font-medium text-emerald-700"
                to={`/finance/customers${rangeQuery}`}
              >
                View all
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-4 py-3">
                        <FinanceHelpLabel label="Customer" />
                      </th>
                    <th className="px-4 py-3">
                        <FinanceHelpLabel label="Net Sales" />
                      </th>
                    <th className="px-4 py-3">
                        <FinanceHelpLabel label="Gross Profit" />
                      </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && (
                    <tr>
                      <td colSpan={3} className="p-2">
                        <TableLoadingSkeleton rows={5} columns={3} />
                      </td>
                    </tr>
                  )}
                  {(topCustomers ?? []).map((customer) => (
                    <tr key={customer.customer_id}>
                      <td className="px-4 py-3">
                        <Link
                          className="font-medium text-emerald-700"
                          to={`/finance/customers/${customer.customer_id}${financeRangeSearch(
                            range,
                            { currency: customer.currency_id },
                          )}`}
                        >
                          {customer.customer_name}
                        </Link>
                        {!customer.complete && <FinanceIncompleteBadge />}
                      </td>
                      <td className="px-4 py-3">
                        {knownMoney(
                          customer.net_sales,
                          customer.known_net_sales,
                          customer.currency,
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {formatMoney(customer.gross_profit, customer.currency)}
                      </td>
                    </tr>
                  ))}
                  {!loading && (topCustomers?.length ?? 0) === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-slate-500" colSpan={3}>
                        No shipped customers in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Production</h2>
              <Link
                className="text-sm font-medium text-emerald-700"
                to={`/finance/production${rangeQuery}`}
              >
                Details
              </Link>
            </div>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Completed WIPs</dt>
                <dd className="font-medium text-slate-900">
                  {report?.production.summary.completed_wips ?? "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Average material cost</dt>
                <dd className="font-medium text-slate-900">
                  {formatMoney(
                    report?.production.summary.average_material_cost_per_wip,
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Top suppliers</h2>
              <Link
                className="text-sm font-medium text-emerald-700"
                to={`/finance/receiving${rangeQuery}`}
              >
                Details
              </Link>
            </div>
            <ol className="mt-4 space-y-3 text-sm">
              {(report?.receiving.top_suppliers ?? []).map((supplier) => (
                <li
                  key={supplier.supplier_id ?? supplier.supplier_name}
                  className="flex justify-between gap-3"
                >
                  <span className="truncate text-slate-600">
                    {supplier.supplier_name}
                  </span>
                  <span className="font-medium text-slate-900">
                    {knownMoney(supplier.spend, supplier.known_spend)}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Inventory stages</h2>
              <Link
                className="text-sm font-medium text-emerald-700"
                to="/finance/inventory"
              >
                Details
              </Link>
            </div>
            <dl className="mt-4 space-y-3 text-sm">
              {(
                [
                  ["raw", "Raw / received"],
                  ["open_wip", "Open WIP"],
                  ["finished", "Finished goods"],
                ] as const
              ).map(([stage, label]) => {
                const value = report?.inventory.stages[stage];
                return (
                  <div key={stage} className="flex justify-between gap-3">
                    <dt className="text-slate-500">{label}</dt>
                    <dd className="font-medium text-slate-900">
                      {value
                        ? knownMoney(value.value, value.known_value)
                        : "—"}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>
        </div>
      </div>
    </main>
  );
}
